import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Button, Message } from 'grab-ui';
import AddInstructionDrawer from '../../drawer/add-instruction-drawer';
import useAddInstruction from '../../../query/useAddInstruction';
import { fetchFullData } from '../../../actions/api-actions';
// eslint-disable-next-line no-unused-vars
import styles from './add-instruction-button.less';
import { useAuthModule } from '../../../hooks/native';
import { getDefaultHeaders } from '../../../common/utils/headers';
import { TOKEN_HEADER } from '../../../constants/headers';
import { DEFAULT_MESSAGE_MARGIN_TOP } from '../../../constants/message';
import { HASH_NUMBER_SEARCH_PARAM } from '../../../constants/app-constants';
import useSearchParams from '../../../hooks/useSearchParams';

const AddInstructionButton = ({
  entry,
  onClick,
  children,
  buttonClassName,
  title,
  address,
  guid,
}) => {
  const { getAccessToken, inApp } = useAuthModule();
  const [searchParams] = useSearchParams();
  const hashNumber = searchParams.get(HASH_NUMBER_SEARCH_PARAM);

  const dispatch = useDispatch();
  const [isOpened, setIsOpened] = useState(false);

  const openDrawer = useCallback(
    () => {
      onClick();
      setIsOpened(true);
    },
    [onClick],
  );
  const closeDrawer = useCallback(() => setIsOpened(false), []);

  const { mutate, isMutating } = useAddInstruction({
    onSuccess: () => {
      if (!inApp) {
        dispatch(fetchFullData(guid, { hashNumber }));
        Message.config({ top: DEFAULT_MESSAGE_MARGIN_TOP });
        Message.success(
          I18n.t('deliveryDetails.instructions.addInstructionsSuccess'),
        );
        closeDrawer();
        return;
      }

      getAccessToken().then(response => {
        const { result } = response;

        const headers = {
          ...getDefaultHeaders(),
          [TOKEN_HEADER]: result,
        };
        dispatch(fetchFullData(guid, { hashNumber }, headers));
        Message.config({ top: DEFAULT_MESSAGE_MARGIN_TOP });
        Message.success(
          I18n.t('deliveryDetails.instructions.addInstructionsSuccess'),
        );
        closeDrawer();
      });
    },
    onError: () => {
      Message.config({ top: DEFAULT_MESSAGE_MARGIN_TOP });
      Message.error(
        I18n.t('deliveryDetails.instructions.addInstructionsFailed'),
      );
      closeDrawer();
    },
  });

  const onSubmit = useCallback(
    text => {
      mutate({ guid, instruction: text, hashNumber });
    },
    [mutate, hashNumber],
  );

  return (
    <div>
      <Button className={buttonClassName} onClick={openDrawer}>
        {children}
      </Button>
      <AddInstructionDrawer
        entry={entry}
        open={isOpened}
        name={title}
        address={address}
        isLoading={isMutating}
        onClose={closeDrawer}
        onSubmit={onSubmit}
      />
    </div>
  );
};

AddInstructionButton.propTypes = {
  entry: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
  buttonClassName: PropTypes.string,
  title: PropTypes.string,
  address: PropTypes.string,
  guid: PropTypes.string,
};

AddInstructionButton.defaultProps = {
  entry: '',
  onClick: () => {},
  children: null,
  buttonClassName: '',
  title: '',
  address: '',
  guid: '',
};

export default AddInstructionButton;
